import { Avatar, Skeleton } from "@mantine/core";
import { useStatusStore } from "@utils/stores/useStatusStore.ts";
import { useTaxProAnalytics } from "@utils/hooks/useAnalytics.ts";
import { IconUser } from "@tabler/icons-react";
import FilterChipsGroup from "../FilterChipsGroup/FilterChipsGroup.tsx";
import FilterChip from "../FilterChip/FilterChip.tsx";
import {
  getConsistentColorForUser,
  getInitials,
} from "../../ClientListTable/utils/assigneeUtils";
import "./AssigneeFilterChips.scss";

interface AssigneeFilterChipsProps {
  members?: any[];
  isLoadingMembers: boolean;
}

const AssigneeFilterChips = ({
  members,
  isLoadingMembers,
}: AssigneeFilterChipsProps) => {
  const { clickedAssignees, setClickedAssignees } = useStatusStore();
  const { assignees: assigneeCounts, isLoadingAnalytics } =
    useTaxProAnalytics();

  const handleAssigneeClick = (memberId: string, count: number) => {
    // Don't allow clicking if count is 0
    if (count === 0) return;

    const isUnassigned = memberId === "null";
    const isCurrentlyChecked = clickedAssignees.includes(memberId);

    if (isUnassigned) {
      // Toggle unassigned filter
      setClickedAssignees(isCurrentlyChecked ? [] : ["null"]);
      return;
    }

    // Toggle assignee filter
    const assigneesWithoutUnassigned = clickedAssignees.filter(
      (id) => id !== "null"
    );
    setClickedAssignees(
      isCurrentlyChecked
        ? assigneesWithoutUnassigned.filter((id) => id !== memberId)
        : [...assigneesWithoutUnassigned, memberId]
    );
  };

  // Create a map of assignee counts for quick lookup
  const assigneeCountsMap = new Map(
    assigneeCounts?.map((a) => [
      a.userId === null ? "null" : a.userId,
      a.count,
    ]) || []
  );

  // Show all members (including those with 0 clients) plus unassigned option
  // Sort by count descending (most clients first), then alphabetically by name
  const allMembers = [
    // Add unassigned option if it exists in analytics
    ...(assigneeCountsMap.has("null")
      ? [
          {
            userId: "null",
            firstName: "Unassigned",
            lastName: "",
            count: assigneeCountsMap.get("null") || 0,
          },
        ]
      : []),
    // Add all members from the members list
    ...(members?.map((member) => ({
      userId: member.userId,
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      count: assigneeCountsMap.get(member.userId) || 0,
    })) || []),
  ].sort((a, b) => {
    // Sort by count descending (highest first)
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    // If counts are equal, sort alphabetically by name
    const nameA = `${a.firstName} ${a.lastName}`.trim().toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  if (isLoadingMembers || isLoadingAnalytics) {
    return (
      <FilterChipsGroup data-testid="assignee-filter-chips-loading">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={36} width={120} radius="xl" />
        ))}
      </FilterChipsGroup>
    );
  }

  return (
    <FilterChipsGroup data-testid="assignee-filter-chips">
      {allMembers.map((member) => {
        const isActive = clickedAssignees.includes(member.userId);
        const isDisabled = member.count === 0;
        const initials = getInitials(member.firstName, member.lastName);
        const avatarColor = getConsistentColorForUser(
          member.userId === "null" ? undefined : member.userId
        );
        const displayName =
          member.userId === "null"
            ? "Unassigned"
            : `${member.firstName} ${member.lastName}`.trim();

        const avatar = (
          <Avatar
            size={24}
            radius="xl"
            color={avatarColor}
            styles={{
              root: {
                backgroundColor: isActive
                  ? "rgba(255,255,255,0.2)"
                  : avatarColor,
                color: isActive ? "white" : "white",
                opacity: isDisabled ? 0.5 : 1,
              },
              placeholder: {
                color: "white",
              },
            }}
          >
            {member.userId === "null" ? (
              <IconUser size={14} />
            ) : (
              <span style={{ fontSize: "11px", fontWeight: 600 }}>
                {initials}
              </span>
            )}
          </Avatar>
        );

        return (
          <FilterChip
            key={member.userId}
            isActive={isActive}
            color={avatarColor}
            icon={avatar}
            onChange={() => handleAssigneeClick(member.userId, member.count)}
            disabled={isDisabled}
            data-testid={`assignee-chip-${member.userId}`}
            customStyles={{
              label: {
                paddingTop: "4px",
                paddingBottom: "4px",
                gap: "8px",
              },
            }}
          >
            {displayName} ({member.count})
          </FilterChip>
        );
      })}
    </FilterChipsGroup>
  );
};

export default AssigneeFilterChips;
